import { useState, useEffect, useCallback } from 'react';
import Icon from '@mdi/react';
import { mdiCalendarEdit } from '@mdi/js';
import { toast } from 'react-toastify';
import BaseModal from '../BaseModal';
import { classApi } from '../../api/classes';
import './SessionOverrideModal.css';

/**
 * SessionOverrideModal
 *
 * Opens from the check-in page for a specific class session on a specific date.
 * Shows who is scheduled vs who actually taught, with checkboxes to override.
 *
 * Props:
 *   classId      string  — the class being checked in
 *   className    string  — display name for the modal title
 *   sessionDate  string  — YYYY-MM-DD, the date of the session
 *   allAdmins    [{ accountId, displayName }]  — all team members
 *   onClose      () → void
 */
const SessionOverrideModal = ({ classId, className, sessionDate, allAdmins, onClose }) => {
    const [session, setSession] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [localInstructors, setLocalInstructors] = useState(new Set());
    const [isSaving, setIsSaving] = useState(false);

    const loadSession = useCallback(async () => {
        setIsLoading(true);
        try {
            // Use next day as 'to' to ensure the session falls within the window regardless of timezone
            const nextDay = new Date(sessionDate + 'T00:00:00');
            nextDay.setDate(nextDay.getDate() + 1);
            const nextDayStr = nextDay.toISOString().slice(0, 10);

            const res = await classApi.getSessions(classId, sessionDate, nextDayStr);
            if (res.ok) {
                const sessions = res.data.sessions || [];
                const found = sessions[0] || null;
                setSession(found || null);
                setLocalInstructors(new Set(found?.actualInstructors || []));
            } else {
                toast.error('Failed to load session');
            }
        } catch {
            toast.error('Failed to load session');
        } finally {
            setIsLoading(false);
        }
    }, [classId, sessionDate]);

    useEffect(() => { loadSession(); }, [loadSession]);

    const toggleInstructor = (accountId) => {
        setLocalInstructors(prev => {
            const next = new Set(prev);
            if (next.has(accountId)) next.delete(accountId);
            else next.add(accountId);
            return next;
        });
    };

    const handleSave = async () => {
        if (!session) return;
        setIsSaving(true);
        try {
            const newActual = [...localInstructors];
            const res = await classApi.updateSessionInstructors(session.id, newActual);
            if (res.ok) {
                toast.success('Instructor override saved');
                onClose(true);
            } else {
                toast.error(res.data?.message || 'Failed to save');
            }
        } catch {
            toast.error('Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    const isDirty = () => {
        if (!session) return false;
        const original = new Set(session.actualInstructors || []);
        if (original.size !== localInstructors.size) return true;
        for (const id of original) { if (!localInstructors.has(id)) return true; }
        return false;
    };

    return (
        <BaseModal
            isOpen
            onClose={onClose}
            title={`Override Instructor — ${className}`}
            icon={mdiCalendarEdit}
            maxWidth={480}
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="base-modal-save-btn"
                        onClick={handleSave}
                        disabled={isSaving || !isDirty() || !session}
                    >
                        {isSaving ? 'Saving...' : 'Save Override'}
                    </button>
                </>
            }
        >
            <div className="session-override-modal">
                {isLoading && (
                    <p className="session-override-modal-loading">Loading session...</p>
                )}

                {!isLoading && !session && (
                    <p className="session-override-modal-empty">
                        No session started yet. Use the Start Session button on the check-in screen to start the session, then swap instructors here.
                    </p>
                )}

                {!isLoading && session && (
                    <>
                        {session.isSubstituted && (
                            <div className="session-override-modal-sub-notice">
                                <span className="session-override-modal-sub-badge">Sub</span>
                                <span className="session-override-modal-sub-note">A substitution was recorded for this session. Update below if needed.</span>
                            </div>
                        )}

                        <div className="session-override-modal-section-label">Who taught this session?</div>
                        <div className="session-override-modal-instructor-checks">
                            {[...allAdmins]
                                .sort((a, b) => {
                                    const aScheduled = (session.scheduledInstructors || []).includes(a.accountId);
                                    const bScheduled = (session.scheduledInstructors || []).includes(b.accountId);
                                    if (aScheduled !== bScheduled) return aScheduled ? -1 : 1;
                                    return (a.displayName || '').split(' ')[0].localeCompare((b.displayName || '').split(' ')[0]);
                                })
                                .map(a => (
                                <label key={a.accountId} className="session-override-modal-check-label">
                                    <input
                                        type="checkbox"
                                        checked={localInstructors.has(a.accountId)}
                                        onChange={() => toggleInstructor(a.accountId)}
                                    />
                                    <span>{a.displayName}</span>
                                    {(session.scheduledInstructors || []).includes(a.accountId) && (
                                        <span className="session-override-modal-scheduled-tag">scheduled</span>
                                    )}
                                </label>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </BaseModal>
    );
};

export default SessionOverrideModal;
